import { useStorage } from 'vue3-storage';
import { User as SentryUser } from '@sentry/vue';
import router from '@/router';
import Api from './Api';
import { ApiRequestParams, ApiResponse, RegisterToken, RegisterTokenSchema } from '@/types/api.type.ts';
import { CurrentUser, CurrentUserSchema } from '@/types/user.type.ts';

const $storage = useStorage();

class Auth {
    private $Api: Api;
    private token?: string;
    private currentUser?: CurrentUser;

    constructor($Api: Api) {
        this.$Api = $Api;
        this.token = $storage.getStorageSync('token');

        if (this.token) {
            this.fetchCurrentUser();
        }
    }

    async getRegisterToken(): Promise<RegisterToken> {
        try {
            const response: any = await this.$Api.callNew('registerToken');

            const parsedRegisterToken = RegisterTokenSchema.parse(response.data);

            return parsedRegisterToken as unknown as RegisterToken;
        }
        catch (error) {
            console.error('Ошибка при получении регистрационного токена:', error);

            throw error;
        }
    }

    async registerUser(registrationData: {
        email: string,
        dealer_sap_code?: string,
        last_name: string,
        first_name: string,
        second_name: string,
        position: string,
        phone: string,
        password: string,
        password_confirmation: string,
        form_token: string,
        accept_auction_rules: boolean,
        accept_rules_processing_personal_data: boolean,
    }): Promise<ApiResponse<{ message: string }>> {
        try {
            const params: ApiRequestParams = {
                requestBody: registrationData,
            };

            const response: any = await this.$Api.callNew('register', params);

            return response.data;
        }
        catch (error) {
            console.error('Ошибка при регистрации пользователя:', error);

            throw error;
        }
    }

    async loginUser(loginData: {
        email: string,
        password: string,
        remember_me?: boolean,
    }): Promise<ApiResponse<{ access_token: string }>> {
        const params: ApiRequestParams = {
            requestBody: {
                login: loginData.email,
                password: loginData.password,
                remember_me: loginData.remember_me ? '1' : '0',
            },
        };

        try {
            const response: any = await this.$Api.callNew('login', params);
            this.token = response.data.access_token;
            $storage.setStorageSync('token', this.token);

            this.$Api.setToken(this.token);

            await this.fetchCurrentUser();

            return response;
        }
        catch (error) {
            console.error('Ошибка при аутентификации пользователя:', error);

            throw error;
        }
    }

    async fetchCurrentUser(): Promise<void> {
        try {
            const response: any = await this.$Api.callNew('getCurrentUser');

            const parsedCurrentUser = CurrentUserSchema.parse(response.data);
            this.currentUser = parsedCurrentUser as unknown as CurrentUser;

            $storage.setStorageSync('current_user', {
                ...this.currentUser,
                // is_admin: true,
            });
        }
        catch (error) {
            console.error('Ошибка при получении текущего пользователя:', error);

            throw error;
        }
    }

    getCurrentUser(): CurrentUser {
        if (!this.currentUser) {
            const parsedCurrentUser = CurrentUserSchema.parse($storage.getStorageSync('current_user'));
            this.currentUser = parsedCurrentUser as unknown as CurrentUser;
        }

        return this.currentUser;
    }

    logout(): void {
        $storage.clearStorageSync();

        this.token = undefined;
        this.currentUser = undefined;

        router.push({ name: 'Login' });
    }

    getCurrentUserForSentry(): SentryUser {
        if (this.token) {
            return {
                id: this.getCurrentUser().id,
                username: this.getCurrentUser().email,
                email: this.getCurrentUser().email,
            };
        }

        return {};
    }

    isLoggedIn(): boolean {
        return !!this.token;
    }
}

export default Auth;
